import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import FieldInput from './FieldInput';
import Tooltip from '../../misc/Tooltip';
import MultiSelect from '../../misc/Multiselect';

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right: 0;
  top: 0;
`;

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100%;
  border: 0.05em solid ${({ borderColor }) => borderColor || '#777'};
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.5em;
  outline: none;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:focus {
    border-color: ${({ primaryColor, disabled }) => (!disabled ? primaryColor : 'none')};
  }};
  }
  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '')}
`;

class MultiselectGenericField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    value: PropTypes.string,
    disabled: PropTypes.bool,
    options: PropTypes.array,
    tabIndex: PropTypes.number,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    // Compact mode for InputGroup
    compactMode: PropTypes.bool,

  }

  render() {

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      options,
      tabIndex,

      //SDK Styles
      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      fieldMode,
      pageLayoutMode,
      fieldPresentationMode,
      compactMode,

    } = this.props;

    const formattedOptions = options && options.length > 0 ? options
      .filter((option) => !option.deleted).map((opt) => ({
        value: opt._id,
        label: opt.value,
        backgroundColor: opt.styles?.backgroundColor || '#f0f0f0',
      })) : [];

    const displayFieldNode = (
      <MultiSelect
        multiple
        tabIndex={tabIndex}
        value={formattedOptions.filter((opt) => value?.includes(opt.value))} // MultiSelect expects selected options
        options={formattedOptions}
        disabled={disabled}
        borderRadius={borderRadius}
        borderColor={borderColor}
        borderWidth={borderWidth}
        primaryColor={primaryColor}
        fontColor={fontColor}
        fontSize={fontSize}
        backgroundColor={backgroundColor}
        compactMode={compactMode}
        onChange={(selectedOptions) => {
          const newValue = selectedOptions.map((opt) => opt.value);
          if (!disabled && newValue !== value) {
            onChange(fieldId, fieldIdentifier, fieldPositionId, newValue);
          }
        }}
        onFocus={() => {
          if (!disabled && onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});
        }}
        onBlur={() => {
          if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
        }}
      />
    );

    return (
      <StyledWrapper {...{ margin }}>
        <FieldTitle
          {...{
            title,
            titleDisplay,
            titleFontSize,
            titleFontColor,
            titleTextAlign,
            titleFontStyle,
            titleFontWeight,
            titleTextTransform,
            titleTextDecoration,

          }}
        />
        {displayFieldNode}
        {this.getTooltip({ ...this.props })}
      </StyledWrapper>
    );
  }

   getTooltip = (props) => {

     const { disabled, fieldId, tipTitle, tipDescription, tipVisible } = props;
     if (disabled) return null;

     return (
       <StyledTooltipWrapper>
         <Tooltip
           fieldId={fieldId}
           title={tipTitle}
           description={tipDescription}
           tipVisible={tipVisible}
         />
       </StyledTooltipWrapper>
     );

   };

   getTextColor = (bgColor) => {
     if (!bgColor) return '#000'; // Default to black if no background color

     // Convert hex to RGB
     const r = parseInt(bgColor.substring(1, 3), 16);
     const g = parseInt(bgColor.substring(3, 5), 16);
     const b = parseInt(bgColor.substring(5, 7), 16);

     // Calculate luminance
     const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

     // Return white for dark backgrounds, black for light backgrounds
     return luminance > 150 ? '#000' : '#FFF';
   };
}

export default MultiselectGenericField;
